/**
 * 個別説明 申込リストの展開詳細 (2026-07-24 追加)。
 * 申込 Deal に紐づく form_submissions の回答内容を、フォームビルダー>回収の
 * 一覧 UI と同じ「質問ラベル → 回答」の 2 カラム dl で表示する。
 *
 * 回収一覧との差分:
 *  - briefing_schedule_picker の回答 (枠 id) は briefing_schedules を引いて
 *    「2026-07-28 11:00〜12:00 (オンライン)」の読める表記に変換する
 *  - 参加人数 (FormSubmission.briefingParticipantCount) をメタ行に表示する
 *
 * 顧客>個別説明 / SAポータル>個別説明 の申込タブ (BriefingsPage) と記録ページから使用。
 *
 * 取得経路 (2026-09-09):
 *  - owner / editor: 従来どおりクライアントから form_submissions / forms を直読み
 *  - 外部SA (level='external'): rules で直読み不可のため Callable
 *    getBriefingSubmissionsForSa を呼ぶ。「自分に関係する申込」(自分の枠が pending 候補 /
 *    自分が担当の確定セッション) のみ返り、ip / referrer / userAgent は含まれない。
 */
import type { FieldFormElement, Form, FormSubmission } from '@goenhub/shared';
import {
  BRIEFING_DELETED_SLOT_LABEL,
  BRIEFING_STYLE_LABEL,
  parseBriefingSlotValue,
  resolveBriefingStyle,
} from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';

import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import {
  type BriefingScheduleRecord,
  type BriefingSubmissionScheduleForSa,
  getBriefingSubmissionsForSaFn,
  useDeletedBriefingScheduleIds,
} from '@/hooks/useBriefings';
import { type DealRecord } from '@/hooks/useDeals';
import { db } from '@/lib/firebase';

interface SubmissionDetail extends Pick<
  FormSubmission,
  | 'formId'
  | 'submittedAt'
  | 'email'
  | 'briefingParticipantCount'
  | 'values'
  | 'ipAddress'
  | 'referrer'
  | 'userAgent'
> {
  id: string;
}

/** ラベル解決に必要な最小限の設問定義 (直読み時は FieldFormElement をそのまま使う)。 */
type FieldDef = Pick<FieldFormElement, 'id' | 'label' | 'fieldType'>;

interface DetailsData {
  submissions: SubmissionDetail[];
  /** formId → 質問フィールド定義 (ラベル解決用) */
  formFields: Map<string, FieldDef[]>;
  /** 外部SA 経路: Callable が返した枠情報 (props.schedules に無い他SA の枠を補完) */
  extraSchedules: Map<string, BriefingSubmissionScheduleForSa>;
}

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function formatDateTime(v: unknown): string {
  const d = tsToDate(v);
  if (!d) return '—';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

interface Props {
  deal: DealRecord;
  /** 枠 id → 日程表記の解決用 (BriefingsPage が保有する一覧を渡す) */
  schedules: BriefingScheduleRecord[];
}

export function BriefingApplicationDetails({ deal, schedules }: Props) {
  const { user } = useAuth();
  // BriefingsPage と同じ判定。外部SA は Callable 経由 (rules で直読み不可)。
  const isExternalSa = user?.level === 'external' && !user?.isOwner;
  const subIds = (deal.sourceFormSubmissionIds ?? []).slice(0, 10);
  // 外部SA は sourceFormSubmissionIds を持たない旧 Deal でも候補/セッション側の
  // formSubmissionId から判定できるため、Deal id があれば常に問い合わせる。
  const enabled = isExternalSa ? !!deal.id : subIds.length > 0;
  const { data, isLoading } = useQuery({
    queryKey: ['briefing_application_details', deal.id, isExternalSa ? 'sa' : subIds.join(',')],
    enabled,
    queryFn: async (): Promise<DetailsData> => {
      if (isExternalSa) {
        const r = await getBriefingSubmissionsForSaFn({ dealId: deal.id });
        const res = r.data;
        return {
          submissions: res.submissions.map((s) => ({
            id: s.id,
            formId: s.formId,
            submittedAt: s.submittedAt ?? '',
            email: s.email ?? undefined,
            briefingParticipantCount: s.briefingParticipantCount,
            values: s.values ?? {},
          })),
          formFields: new Map(Object.entries(res.formFields ?? {})),
          extraSchedules: new Map(Object.entries(res.schedules ?? {})),
        };
      }
      const submissions: SubmissionDetail[] = [];
      for (const sid of subIds) {
        try {
          const snap = await getDoc(doc(db, 'form_submissions', sid));
          if (snap.exists()) {
            submissions.push({ id: snap.id, ...(snap.data() as FormSubmission) });
          }
        } catch {
          // 権限不足・削除済み等はスキップ
        }
      }
      // 新しい順
      submissions.sort(
        (a, b) =>
          (tsToDate(b.submittedAt)?.getTime() ?? 0) - (tsToDate(a.submittedAt)?.getTime() ?? 0),
      );
      const formFields = new Map<string, FieldFormElement[]>();
      const formIds = [...new Set(submissions.map((s) => s.formId).filter(Boolean))];
      for (const fid of formIds) {
        try {
          const fsnap = await getDoc(doc(db, 'forms', fid));
          if (fsnap.exists()) {
            const form = fsnap.data() as Form;
            formFields.set(
              fid,
              (form.elements ?? []).filter((e): e is FieldFormElement => e.kind === 'field'),
            );
          }
        } catch {
          // 権限不足等はラベル無しで id 表示にフォールバック
        }
      }
      return { submissions, formFields, extraSchedules: new Map() };
    },
  });

  // 回答中の候補日のうち削除済みの枠 (id の代わりに「削除された候補日」と表記する)
  const deletedScheduleIds = useDeletedBriefingScheduleIds(
    (data?.submissions ?? []).flatMap((sub) =>
      (data?.formFields.get(sub.formId) ?? [])
        .filter((f) => f.fieldType === 'briefing_schedule_picker')
        .flatMap((f) => {
          const v = sub.values?.[f.id];
          return (Array.isArray(v) ? v : v ? [v] : []).map(
            (raw) => parseBriefingSlotValue(raw).scheduleId,
          );
        }),
    ),
  );

  // 枠 id → 「2026-07-28 11:00〜12:00 (オンライン @ 場所)」
  // 回答値は「両方可」枠のみ `${枠id}::online` 形式 (顧客が選んだ形式を保持)
  const scheduleLabel = (rawValue: string): string => {
    const { scheduleId, style: picked } = parseBriefingSlotValue(rawValue);
    const s = schedules.find((x) => x.id === scheduleId) ?? data?.extraSchedules.get(scheduleId);
    if (!s) return deletedScheduleIds.has(scheduleId) ? BRIEFING_DELETED_SLOT_LABEL : rawValue;
    const style = resolveBriefingStyle(s.style, picked);
    const place = style !== 'online' && s.location ? ` @ ${s.location}` : '';
    return `${s.date} ${s.startTime}〜${s.endTime} (${BRIEFING_STYLE_LABEL[style]}${place})`;
  };

  const formatFieldValue = (f: FieldDef, v: string | string[] | undefined): string => {
    if (v === undefined) return '';
    const arr = Array.isArray(v) ? v : [v];
    if (f.fieldType === 'briefing_schedule_picker') {
      return arr.map(scheduleLabel).join('\n');
    }
    return arr.join('; ');
  };

  if (!enabled) {
    return (
      <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs text-[var(--text-muted)]">
        紐づくフォーム回答がありません
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)] p-3">
        <Spinner />
      </div>
    );
  }
  if (!data || data.submissions.length === 0) {
    return (
      <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs text-[var(--text-muted)]">
        {isExternalSa
          ? '閲覧できる申込内容がありません (自分の枠が候補になっている申込、または自分が担当する確定済みの申込のみ表示されます)'
          : '回答を読み込めませんでした (削除済み、または閲覧権限がありません)'}
      </div>
    );
  }

  return (
    <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)]">
      {data.submissions.map((s, idx) => {
        const fields = data.formFields.get(s.formId) ?? [];
        return (
          <div
            key={s.id}
            className={`p-3 space-y-2 ${idx > 0 ? 'border-t border-[var(--border)]' : ''}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-[var(--text-muted)]">
                {formatDateTime(s.submittedAt)}
              </span>
              {s.email && <Badge variant="accent">{s.email}</Badge>}
              {s.briefingParticipantCount ? (
                <Badge variant="review">参加{s.briefingParticipantCount}名</Badge>
              ) : null}
            </div>
            {fields.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">
                フォーム定義が見つからないため、ラベルを解決できません
              </p>
            ) : (
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                {fields.map((f) => {
                  const v = formatFieldValue(f, s.values?.[f.id]);
                  return (
                    <div key={f.id} className="min-w-0">
                      <dt className="text-[11px] text-[var(--text-muted)] mb-0.5">{f.label}</dt>
                      <dd className="text-xs text-[var(--text-primary)] whitespace-pre-wrap break-words">
                        {v || <span className="text-[var(--text-muted)]">—</span>}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
            {/* 監査項目 (ip / ref / ua) は管理者のみ。外部SA 経路では返却されない */}
            {!isExternalSa && (
              <div className="pt-2 mt-1 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[var(--text-muted)] font-mono">
                <div>submissionId: {s.id}</div>
                {s.ipAddress && <div>ip: {s.ipAddress}</div>}
                {s.referrer && <div className="truncate">ref: {s.referrer}</div>}
                {s.userAgent && <div className="md:col-span-2 truncate">ua: {s.userAgent}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
